/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-shadow */

import { useGesture, UserHandlers } from "@use-gesture/react";
import { RefObject, useCallback, useEffect, useRef } from "react";
import { IPreviewContext } from "../../contexts/preview-context";
import { useEditor } from "../../hooks/useEditor";
import { useMessage } from "../../hooks/useMessage";
import { useSelectedZone } from "../../hooks/useSelection";
import { Transform } from "../../transform";
import { useGuidelineOverlay } from "../guideline-overlay/use-guideline-overlay";
import { IZoneSelectionBoxRef, ZoneSelectionBoxHandlers } from "../zone-selection-box";
import { usePhotoPanning } from "./hooks/use-photo-panning";
import { usePhotoSwapping } from "./hooks/use-photo-swapping";
import { usePreviewPanning } from "./hooks/use-preview-pan-pinch";
import { useZoneMove } from "./hooks/use-zone-move";
import { useZoneResize } from "./hooks/use-zone-resize";
import type { ISlidesShowPreviewRef } from "./slideshow-preview";

const allZoneSelectionBoxHandlers = [
	ZoneSelectionBoxHandlers.Top,
	ZoneSelectionBoxHandlers.Left,
	ZoneSelectionBoxHandlers.Right,
	ZoneSelectionBoxHandlers.Bottom,
	ZoneSelectionBoxHandlers.TopLeft,
	ZoneSelectionBoxHandlers.TopRight,
	ZoneSelectionBoxHandlers.BottomLeft,
	ZoneSelectionBoxHandlers.BottomRight,
];

/** This will contain the logic to update the zone selection box */
export function useZoneSelectionBox({
	slideShowPreviewRef,
	zoneSelectionBox,
	previewContext,
	allowedZoneHandlers,
	onResize,
}: {
	slideShowPreviewRef: RefObject<ISlidesShowPreviewRef>;
	zoneSelectionBox: IZoneSelectionBoxRef | null;
	previewContext: IPreviewContext;
	allowedZoneHandlers: ZoneSelectionBoxHandlers;
	/** Called when resize starts/ends with the resize handle used */
	onResize(zoneResizingHandle: ZoneSelectionBoxHandlers): void;
}) {
	const editor = useEditor();
	const selectedZone = useSelectedZone();
	const transformableZone = eXpress.core.isTransformableZone(selectedZone)
		? selectedZone
		: undefined;
	const { showDefaultHandlers, updateZoneSelectionBox } = zoneSelectionBox || {};

	/**
	 * Function draw a selection box around a specific zone
	 * - undefined to remove it
	 */
	const updateSelectionBox = useCallback(
		function updateSelectionBox(zone: eXpress.core.IZone | undefined) {
			if (!updateZoneSelectionBox) return;

			// Add/Update ZoneSelectionBox
			const zoneToSelect = eXpress.core.isPageZone(zone) ? undefined : zone;
			const screenToSpreadDipTransform = previewContext.getScreenToSpreadTransform(
				zoneToSelect?.spread,
				undefined,
			);

			updateZoneSelectionBox(
				screenToSpreadDipTransform ? zoneToSelect : undefined,
				screenToSpreadDipTransform || Transform.unit,
			);
		},
		[previewContext, updateZoneSelectionBox],
	);

	const showDefaultHandlersWithTransform = useCallback(
		function showDefaultHandlersWithTransform(zone: eXpress.core.IZone | undefined) {
			if (!showDefaultHandlers) return;

			// Add/Update ZoneSelectionBox
			const zoneToSelect = eXpress.core.isPageZone(zone) ? undefined : zone;
			const screenToSpreadDipTransform = previewContext.getScreenToSpreadTransform(
				zoneToSelect?.spread,
				undefined,
			);

			showDefaultHandlers(
				screenToSpreadDipTransform ? zoneToSelect : undefined,
				screenToSpreadDipTransform || Transform.unit,
				allowedZoneHandlers,
			);
		},
		[previewContext, showDefaultHandlers, allowedZoneHandlers],
	);

	// Update ZoneSelectionBox on slideshow change
	useEffect(
		function updateSelectionBoxOnTransformChange() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			return slideShow.onTransformChange.subscribe(function onTransformChange() {
				const selectedZone = editor.selection.getCurrentZone(); // Currently use selected zone from core
				updateSelectionBox(selectedZone);

				// Hide handlers for small sizes
				showDefaultHandlersWithTransform(selectedZone);
			});
		},
		[editor, slideShowPreviewRef, showDefaultHandlersWithTransform, updateSelectionBox],
	);

	// Update ZoneSelectionBox on selection change
	useEffect(
		function updateSelectionBoxOnZoneChange() {
			updateSelectionBox(selectedZone);
		},
		[selectedZone, showDefaultHandlers, updateSelectionBox],
	);

	// Update handles when
	// - selection changes
	// - when allowed handles change
	useEffect(
		function updateSelectionBoxOnZoneChangeOrAllowedChanged() {
			showDefaultHandlersWithTransform(selectedZone);
		},
		[showDefaultHandlersWithTransform, selectedZone],
	);

	const [, zoneResizer] = useZoneResize();
	const [isPreviewPanning] = usePreviewPanning();
	const [isZoneMoving] = useZoneMove();
	const [isPhotoPanning] = usePhotoPanning({});
	const [isPhotoSwapping] = usePhotoSwapping(undefined);

	const [, useLines] = useGuidelineOverlay();

	// Update selection Box when zone is moved/scaled/rotated during interactive commands
	useMessage(
		useCallback(
			function updateSelectionBoxOnMessage(message) {
				if (
					message instanceof eXpress.core.SpreadRenderedMessage &&
					message.spread.zones.includes(selectedZone as eXpress.core.ITransformableZone)
				) {
					updateSelectionBox(selectedZone);
					showDefaultHandlersWithTransform(selectedZone); // Example: After an Undo
				}
			},
			[selectedZone, showDefaultHandlersWithTransform, updateSelectionBox],
		),
		{ captureInteractiveMessages: true },
	);

	const isTouchEvent = (event: Event): event is TouchEvent => {
		return event instanceof TouchEvent;
	};

	const target = useRef<EventTarget | null>();
	// get point on screen based from the currentTarget, this way the dragposition will always match the handler
	const getPointOnScreen = useCallback<UserHandlers["onDragStart"]>(
		function getPointOnScreenFromEvent(arg) {
			let pointOnScreen: eXpress.math.Point;
			if (isTouchEvent(arg.event)) {
				const touchArray = Array.from(arg.event.touches);
				const originalTouch = touchArray.find(
					(touch) =>
						(touch.target as HTMLElement).offsetParent === target.current ||
						(touch.target as HTMLElement) === target.current,
				);
				if (!originalTouch) return undefined;
				pointOnScreen = eXpress.math.point(originalTouch.clientX, originalTouch.clientY);
			} else {
				const {
					xy: [x, y],
				} = arg;
				pointOnScreen = eXpress.math.point(x, y);
			}

			return pointOnScreen;
		},
		[],
	);

	// Create a loop because useGesture does not accept multiple targets (we cannot use the parent target that has no pointer-event)
	allZoneSelectionBoxHandlers.forEach((handle, index) => {
		const onDragStart = useCallback<UserHandlers["onDragStart"]>(
			function onResizeDragStart(arg) {
				if (!transformableZone) return;
				if (isPreviewPanning) return; // prevent resize when adding second finger on a handle during preview panning
				if (isZoneMoving) return; // prevent resize when adding second finger on a handle during zone moving
				if (isPhotoPanning) return; // prevent resize when adding second finger on a handle during photo panning
				if (isPhotoSwapping) return; // prevent resize when adding second finger on a handle during photo swap

				// set the element on which the initial touch action takes place
				target.current = arg.event.currentTarget;

				const pointOnScreen = getPointOnScreen(arg);
				if (!pointOnScreen) return;
				zoneResizer.start({
					selectedZone: transformableZone,
					pointOnScreen,
					handle,
				});
				useLines.setHandle(handle);
				onResize(handle);
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[
				zoneResizer,
				transformableZone,
				isPreviewPanning,
				isZoneMoving,
				isPhotoPanning,
				isPhotoSwapping,
			],
		);

		const onDrag = useCallback<UserHandlers["onDrag"]>(
			function onResizeDrag(arg) {
				const pointOnScreen = getPointOnScreen(arg);
				if (!pointOnScreen) return;
				zoneResizer.update(pointOnScreen);
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[zoneResizer],
		);

		const onDragEnd = useCallback<UserHandlers["onDragEnd"]>(
			function onResizeDragEnd() {
				zoneResizer.end();
				onResize(ZoneSelectionBoxHandlers.None);
			},
			// eslint-disable-next-line react-hooks/exhaustive-deps
			[zoneResizer],
		);

		// Add handlers
		useGesture(
			{
				onDragStart,
				onDrag,
				onDragEnd,
			},
			{
				target: zoneSelectionBox?.el.children[index],
				drag: {
					pointer: {
						touch: true,
					},
					tapsThreshold: 3,
					// Prevent other default actions like goback on swipe on Safari/Chrome
					preventDefault: true,
					eventOptions: {
						capture: true,
						passive: false,
					},
				},
			},
		);
	});
}
